import type { RacingEvent, Track, SanctioningBody } from '../../types';
import { Field, Input, NumberInput, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@racing/ui';

interface EventFormProps {
  event: Partial<RacingEvent>;
  tracks: Track[];
  sanctioningBodies: SanctioningBody[];
  onChange: (field: keyof RacingEvent, value: string | number | boolean | null) => void;
}

const EVENT_TYPES = ['BRACKET', 'HEADS_UP', 'TEST_AND_TUNE'];
const EVENT_STATUSES = [
  'DRAFT',
  'PUBLISHED',
  'REGISTRATION_OPEN',
  'REGISTRATION_CLOSED',
  'COMPLETED',
  'CANCELLED',
];

const humanise = (value: string) => value.replace(/_/g, ' ');

/** Radix Select has no empty-string value, so "none" stands in for null. */
const NONE = '__none__';

const EventForm = ({ event, tracks, sanctioningBodies, onChange }: EventFormProps) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <Field label="Event Name" required className="sm:col-span-2 lg:col-span-4">
      {(p) => (
        <Input
          value={event.event_name || ''}
          onChange={(e) => onChange('event_name', e.target.value)}
          required
          {...p}
        />
      )}
    </Field>

    <Field label="Sanctioning Body" className="sm:col-span-1 lg:col-span-2">
      {(p) => (
        <Select
          value={event.sanctioning_body_id ? String(event.sanctioning_body_id) : NONE}
          onValueChange={(value) =>
            onChange('sanctioning_body_id', value === NONE ? null : Number(value))
          }
        >
          <SelectTrigger id={p.id}>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>None</SelectItem>
            {sanctioningBodies.map((sb) => (
              <SelectItem key={sb.sanctioning_body_id} value={String(sb.sanctioning_body_id)}>
                {sb.abbreviation ? `${sb.body_name} (${sb.abbreviation})` : sb.body_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>

    <Field label="Track" required className="sm:col-span-1 lg:col-span-2">
      {(p) => (
        <Select
          value={event.track_id ? String(event.track_id) : ''}
          onValueChange={(value) => onChange('track_id', Number(value))}
        >
          <SelectTrigger id={p.id}>
            <SelectValue placeholder="Select a track" />
          </SelectTrigger>
          <SelectContent>
            {tracks.map((track) => (
              <SelectItem key={track.track_id} value={String(track.track_id)}>
                {track.track_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>

    <Field label="Event Type">
      {(p) => (
        <Select
          value={event.event_type || 'BRACKET'}
          onValueChange={(value) => onChange('event_type', value)}
        >
          <SelectTrigger id={p.id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EVENT_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {humanise(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>

    <Field label="Status">
      {(p) => (
        <Select
          value={event.status || 'DRAFT'}
          onValueChange={(value) => onChange('status', value)}
        >
          <SelectTrigger id={p.id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EVENT_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {humanise(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>

    <Field label="Max Entries" hint="Blank means no cap">
      {(p) => (
        <NumberInput
          value={event.max_entries ?? null}
          onValueChange={(value) => onChange('max_entries', value)}
          integer
          {...p}
        />
      )}
    </Field>

    <div className="hidden lg:block" />

    <Field label="Start Date" required>
      {(p) => (
        <Input
          type="date"
          value={event.start_date ? String(event.start_date).substring(0, 10) : ''}
          onChange={(e) => onChange('start_date', e.target.value)}
          required
          {...p}
        />
      )}
    </Field>

    <Field label="End Date" required>
      {(p) => (
        <Input
          type="date"
          value={event.end_date ? String(event.end_date).substring(0, 10) : ''}
          onChange={(e) => onChange('end_date', e.target.value)}
          required
          {...p}
        />
      )}
    </Field>

    <Field label="Registration Open">
      {(p) => (
        <Input
          type="datetime-local"
          value={
            event.registration_open_date
              ? String(event.registration_open_date).substring(0, 16)
              : ''
          }
          onChange={(e) => onChange('registration_open_date', e.target.value || null)}
          {...p}
        />
      )}
    </Field>

    <Field label="Registration Close">
      {(p) => (
        <Input
          type="datetime-local"
          value={
            event.registration_close_date
              ? String(event.registration_close_date).substring(0, 16)
              : ''
          }
          onChange={(e) => onChange('registration_close_date', e.target.value || null)}
          {...p}
        />
      )}
    </Field>

    <Field label="Notes" className="sm:col-span-2 lg:col-span-4">
      {(p) => (
        <Textarea
          rows={3}
          value={event.notes || ''}
          onChange={(e) => onChange('notes', e.target.value)}
          {...p}
        />
      )}
    </Field>
  </div>
);

export default EventForm;
